import React, { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { Checkbox, FormHelperText, IconButton, Link, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  DivContentBodyStyled,
  DivContentHeaderStyled,
  DivContentStyled,
  DivInnerQuoteStyled,
  DivPolicyStyled,
  DivQuoteStyled,
  DivRootStyled,
  FormStyled,
  GridContainerStyled,
  GridContentStyled,
  GridQuoteContainerStyled,
  SignUpButtonStyled,
  TextFieldStyled,
  TypographyPersonBioStyled,
  TypographyPersonNameStyled,
  TypographyQuoteTextStyled,
  TypographyTitleStyled
} from "./SignUp.styles";
const SignUp = () => {
  const navigate = useNavigate();
  const [formState, setFormState] = useState({
    isValid: false,
    values: {},
    touched: {},
    errors: {}
  });
  const signUpSchema = React.useMemo(() => {
    return Yup.object().shape({
      firstName: Yup.string().max(32).required(),
      lastName: Yup.string().max(32).required(),
      email: Yup.string().email().max(64).required(),
      password: Yup.string().max(128).required(),
      policy: Yup.bool().oneOf([true])
    });
  }, []);
  const validateFormStateValues = React.useCallback(
    async (values) => {
      try {
        await signUpSchema.validate(values);
      } catch (errors) {
        return errors;
      }
      return null;
    },
    [signUpSchema]
  );
  React.useEffect(() => {
    validateFormStateValues(formState.values).then((errors) => {
      setFormState((formState2) => ({
        ...formState2,
        isValid: !errors,
        errors: errors || {}
      }));
    });
  }, [formState.values, validateFormStateValues]);
  const handleChange = (event) => {
    event.persist();
    setFormState((formState2) => ({
      ...formState2,
      values: {
        ...formState2.values,
        [event.target.name]: event.target.type === "checkbox" ? event.target.checked : event.target.value
      },
      touched: {
        ...formState2.touched,
        [event.target.name]: true
      }
    }));
  };
  const handleBack = () => {
    navigate(-1);
  };
  const handleSignUp = (event) => {
    event.preventDefault();
    navigate("/");
  };
  const hasError = (field) => !!(formState.touched[field] && formState.errors[field]);
  return /* @__PURE__ */ React.createElement(DivRootStyled, null, /* @__PURE__ */ React.createElement(GridContainerStyled, { container: true }, /* @__PURE__ */ React.createElement(GridQuoteContainerStyled, { item: true, lg: 5 }, /* @__PURE__ */ React.createElement(DivQuoteStyled, null, /* @__PURE__ */ React.createElement(DivInnerQuoteStyled, null, /* @__PURE__ */ React.createElement(TypographyQuoteTextStyled, { variant: "h1" }), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(TypographyPersonNameStyled, { variant: "body1" }), /* @__PURE__ */ React.createElement(TypographyPersonBioStyled, { variant: "body2" }))))), /* @__PURE__ */ React.createElement(GridContentStyled, { item: true, lg: 7, xs: 12 }, /* @__PURE__ */ React.createElement(DivContentStyled, null, /* @__PURE__ */ React.createElement(DivContentHeaderStyled, null, /* @__PURE__ */ React.createElement(IconButton, { size: "large", onClick: handleBack }, /* @__PURE__ */ React.createElement(ArrowBackIcon, null))), /* @__PURE__ */ React.createElement(DivContentBodyStyled, null, /* @__PURE__ */ React.createElement(FormStyled, { onSubmit: handleSignUp }, /* @__PURE__ */ React.createElement(TypographyTitleStyled, { variant: "h2" }, "Create new account"), /* @__PURE__ */ React.createElement(Typography, { color: "textSecondary", gutterBottom: true }, "Use your email to create new account"), /* @__PURE__ */ React.createElement(
    TextFieldStyled,
    {
      error: hasError("firstName"),
      fullWidth: true,
      helperText: hasError("firstName") ? formState.errors.firstName[0] : null,
      label: "First name",
      name: "firstName",
      onChange: handleChange,
      type: "text",
      value: formState.values.firstName || "",
      variant: "outlined"
    }
  ), /* @__PURE__ */ React.createElement(
    TextFieldStyled,
    {
      error: hasError("lastName"),
      fullWidth: true,
      helperText: hasError("lastName") ? formState.errors.lastName[0] : null,
      label: "Last name",
      name: "lastName",
      onChange: handleChange,
      type: "text",
      value: formState.values.lastName || "",
      variant: "outlined"
    }
  ), /* @__PURE__ */ React.createElement(
    TextFieldStyled,
    {
      error: hasError("email"),
      fullWidth: true,
      helperText: hasError("email") ? formState.errors.email[0] : null,
      label: "Email address",
      name: "email",
      onChange: handleChange,
      type: "text",
      value: formState.values.email || "",
      variant: "outlined"
    }
  ), /* @__PURE__ */ React.createElement(
    TextFieldStyled,
    {
      error: hasError("password"),
      fullWidth: true,
      helperText: hasError("password") ? formState.errors.password[0] : null,
      label: "Password",
      name: "password",
      onChange: handleChange,
      type: "password",
      value: formState.values.password || "",
      variant: "outlined"
    }
  ), /* @__PURE__ */ React.createElement(DivPolicyStyled, null, /* @__PURE__ */ React.createElement(
    Checkbox,
    {
      checked: formState.values.policy || false,
      sx: { marginLeft: "-14px" },
      color: "primary",
      name: "policy",
      onChange: handleChange
    }
  ), /* @__PURE__ */ React.createElement(Typography, { color: "textSecondary", variant: "body1" }, "I have read the ", /* @__PURE__ */ React.createElement(Link, { color: "primary", component: RouterLink, to: "#", underline: "always", variant: "h6" }, "Terms and Conditions"))), hasError("policy") && /* @__PURE__ */ React.createElement(FormHelperText, { error: true }, formState.errors.policy[0]), /* @__PURE__ */ React.createElement(SignUpButtonStyled, { disabled: !formState.isValid, fullWidth: true, size: "large", type: "submit" }, "Sign up now"), /* @__PURE__ */ React.createElement(Typography, { color: "textSecondary", variant: "body1" }, "Have an account? ", /* @__PURE__ */ React.createElement(Link, { component: RouterLink, to: "/sign-in", variant: "h6" }, "Sign in"))))))));
};
SignUp.propTypes = {
  history: PropTypes.object
};
export default SignUp;
